import React, { useEffect, useRef, useState } from "react";
import { Select } from "antd";
import axios from "axios";
import { linyuanApi } from "../../common/javascripts/common.js";

const defaultPageSize = 10;
const defaultDebounceInterval = 1000;

/*
* 从API获取数据并渲染为Select组件
* @param {string} apiUrl - API地址
* @param {string} labelKey - 选项标签对应的键名
* @param {string} searchKey - 搜索参数对应的键名(可选，默认与labelKey相同，该字段一半用于接口的多字段拼接字符串模糊查询)
* @param {string} valueKey - 选项值对应的键名
* @param {number} [pageSize=10] - 每页数据量
* @param {string} [mode="multiple"] - 选择模式，可选值为"multiple"（多选）或"default"（单选）
* @param {boolean} [allowClear=true] - 是否允许清空选择
* @param {any} [value] - 初始选中值
* @param {function} [onChange] - 选中值变化时的回调函数
* @param {number} [debounceInterval=1000] - 防抖间隔，单位为毫秒
* @param {string} [placeholder] - 占位符
* @param {boolean} [disabled=false] - 是否禁用
* @param {object} [params={}] - 额外的查询参数
* @param {function} [onOptionsLoaded] - 数据加载完成后的回调函数
* @
*/
export default function ApiSelect(props) {
  const {
    apiUrl,
    labelKey,
    searchKey = 'search',
    valueKey,
    pageSize = defaultPageSize,
    mode,
    allowClear = true,
    value,
    onChange,
    debounceInterval = defaultDebounceInterval,
    placeholder,
    disabled,
    params: extraParams = {},
    onOptionsLoaded,
    style,
    ...rest
  } = props;

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef("");
  const debounceRef = useRef(null);

  const fetchOptions = async (keyword) => {
    try {
      setLoading(true);
      const baseParams = { pageSize, ...extraParams };
      const trimmed = typeof keyword === "string" ? keyword.trim() : "";
      if (trimmed) {
        baseParams[searchKey] = trimmed;
      }
      let res;
      if (typeof apiUrl === "string" && (apiUrl.startsWith("http://") || apiUrl.startsWith("https://"))) {
        res = await axios.get(apiUrl, { params: baseParams });
      } else {
        res = await linyuanApi.get(apiUrl, { params: baseParams });
      }
      const payload = res && res.data !== undefined ? res.data : res;
      let list = [];
      if (Array.isArray(payload)) {
        list = payload;
      } else if (Array.isArray(payload.data)) {
        list = payload.data;
      } else if (Array.isArray(payload.list)) {
        list = payload.list;
      } else if (payload.data && Array.isArray(payload.data.list)) {
        list = payload.data.list;
      }
      const mapped = list
        .map((item) => {
          const label = item[labelKey];
          const optionValue = item[valueKey];
          if (label === undefined || label === null) {
            return null;
          }
          if (optionValue === undefined || optionValue === null) {
            return null;
          }
          return {
            label,
            value: optionValue,
          };
        })
        .filter(Boolean);
      setOptions(mapped);
      if (onOptionsLoaded) {
        onOptionsLoaded(mapped);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions("");
  }, [apiUrl, pageSize, labelKey, valueKey, JSON.stringify(extraParams)]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleSearch = (val) => {
    searchRef.current = val;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      fetchOptions(searchRef.current);
    }, debounceInterval);
  };

  const handleChange = (val) => {
    if (!onChange) {
      return;
    }
    onChange(val);
  };

  return (
    <Select
      showSearch
      filterOption={false}
      options={options}
      loading={loading}
      mode={mode}
      allowClear={allowClear}
      value={value}
      onChange={handleChange}
      onSearch={handleSearch}
      placeholder={placeholder}
      disabled={disabled}
      style={style || { width: 260 }}
      {...rest}
    />
  );
}
